import '@fontsource/outfit/400.css';
import '@fontsource/outfit/500.css';
import '@fontsource/outfit/600.css';
import '@fontsource/outfit/700.css';
import '@aws-amplify/ui-react/styles.css';
import '@aws-amplify/ui-react-liveness/styles.css';
import '@/styles/index.sass';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { Amplify } from 'aws-amplify';
import { initAnalytics } from '@/firebase/init';
import i18n, { COMMON_NAMESPACE } from '@/i18n';
import { TenantBrandingGate } from '@/components/TenantBrandingGate/TenantBrandingGate';
import { storeInviteTokenFromLocation } from '@/helpers/inviteToken';
import { fetchSelfieConfig } from '@/helpers/selfieConfig';
import { fetchTenantBranding } from '@/helpers/tenantBranding';
import { AppRoutes } from '@/routes/AppRoutes';
import { createOnboardingTheme } from '@/theme/theme';

async function bootstrap(): Promise<void> {
  // ?convite= (QR de /mobile) persiste o token do link único antes do 1º render.
  storeInviteTokenFromLocation(window.location);

  const [selfieConfig, tenantBranding] = await Promise.all([
    fetchSelfieConfig(),
    fetchTenantBranding(),
  ]);

  Amplify.configure({
    Auth: {
      Cognito: {
        identityPoolId: selfieConfig.identityPoolId,
        allowGuestAccess: true,
      },
    },
  });

  void initAnalytics();

  const root = document.getElementById('root');
  if (!root) {
    throw new Error('root element not found');
  }

  const theme =
    tenantBranding.status === 'ok'
      ? createOnboardingTheme(tenantBranding.config.branding)
      : createOnboardingTheme();

  // FaceLivenessDetector uses WebRTC/event streams that break under React StrictMode
  // double-mount (ReadableStream "Cannot cancel a locked stream"). See amplify-ui#6245.
  createRoot(root).render(
    <I18nextProvider i18n={i18n}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <TenantBrandingGate notFound={tenantBranding.status === 'not-found'}>
            <AppRoutes />
          </TenantBrandingGate>
        </BrowserRouter>
      </ThemeProvider>
    </I18nextProvider>,
  );
}

/**
 * Bootstrap que falha = app sem tela. Antes isso virava o texto CRU do erro
 * ("Falha ao carregar configuração de selfie: 500") num documento sem estilo, e
 * a única saída era o convidado recarregar por conta própria — que é
 * exatamente o que "funcionava na segunda vez". Agora a falha explica o que
 * houve e traz o botão de tentar de novo.
 */
function renderBootstrapFailure(): void {
  const root = document.getElementById('root');
  if (!root) return;

  root.replaceChildren();

  const container = document.createElement('div');
  container.setAttribute('role', 'alert');
  container.dataset.testid = 'bootstrap-error';
  container.style.cssText =
    'display:flex;flex-direction:column;align-items:center;justify-content:center;' +
    'gap:16px;min-height:100dvh;padding:24px;text-align:center;' +
    'font-family:Outfit,system-ui,sans-serif;color:#1a1a1a';

  const title = document.createElement('h1');
  title.textContent = i18n.t('bootstrapError', { ns: COMMON_NAMESPACE });
  title.style.cssText = 'font-size:20px;font-weight:600;margin:0';

  const detail = document.createElement('p');
  detail.textContent = i18n.t('bootstrapErrorDetail', { ns: COMMON_NAMESPACE });
  detail.style.cssText = 'font-size:15px;line-height:1.5;margin:0;max-width:32ch;opacity:.75';

  const retry = document.createElement('button');
  retry.type = 'button';
  retry.textContent = i18n.t('bootstrapRetry', { ns: COMMON_NAMESPACE });
  retry.style.cssText =
    'border:0;border-radius:999px;padding:12px 28px;font:inherit;font-weight:600;' +
    'background:#1a1a1a;color:#fff;cursor:pointer';
  retry.addEventListener('click', () => {
    window.location.reload();
  });

  container.append(title, detail, retry);
  root.append(container);
}

void bootstrap().catch((error: unknown) => {
  renderBootstrapFailure();
  throw error;
});
